import React from 'react';

/** One-line label for pass cards: all eligible venue names, or primary `restaurantDetails` name. */
export function getPassVenueNamesLine(pass) {
  if (!pass) return '';
  const names =
    pass.eligibleRestaurantDetails
      ?.map((r) => r?.name)
      .filter(Boolean) ?? [];
  const unique = [...new Set(names)];
  if (unique.length > 0) return unique.join(' · ');
  return pass.restaurantDetails?.name || '';
}

/**
 * Unified pass filtering logic to only show user's unused passes
 * 
 * @param {Array} availablePasses - All available passes
 * @param {Array} userPassClaims - User's pass claims for current week
 * @param {Object} options - Filtering options
 * @param {Array} options.restaurants - Restaurants data (for MapScreen)
 * @param {Array} options.userMemberships - User's memberships (for MapScreen)
 * @param {Array} options.userPointsForRestaurants - User's points per restaurant (for MapScreen)
 * @param {string} options.selectedDay - Selected day for filtering (for MapScreen)
 * @param {Object} options.selectedRestaurant - Selected restaurant (for PassesComponent)
 * @param {Object} options.userTier - User's tier (for PassesComponent)
 * @param {number} options.userPoints - User's points (for PassesComponent)
 * @returns {Object} Categorized passes: { down: [], location: [], others: [] }
 */
export const filterUnusedPasses = (availablePasses, userPassClaims, options = {}) => {
  if (!availablePasses || userPassClaims === undefined) return { down: [], location: [], others: [] };

  const {
    restaurants,
    userMemberships,
    userPointsForRestaurants,
    selectedDay,
    selectedRestaurant,
    userTier,
    userPoints
  } = options;

  // Create sets for quick lookup of different claim types
  const downPassIds = new Set();
  const claimedPassIds = new Set();
  const giftedPassIds = new Set();

  userPassClaims.forEach(claim => {
    if (claim.actionType === "down") {
      downPassIds.add(claim.passId);
    } else if (claim.actionType === "claimed") {
      claimedPassIds.add(claim.passId);
    } else if (claim.actionType === "shared") {
      giftedPassIds.add(claim.passId);
    }
  });

  // Tier hierarchy for accessibility checks
  const tierHierarchy = {
    "Discovery": 0,
    "Silver": 1,
    "Gold": 2,
    "Platinum": 3,
    "Diamond": 4
  };

  // Helper function to check if a pass is valid for the selected day
  const isPassValidForDay = (pass, day) => {
    if (day === 'All') return true;
    
    // If pass has time constraints, check if it's valid for the selected day
    if (pass.startTime && pass.endTime) {
      // The timestamps actually contain date information (from when the migration was run)
      // Extract the day from the startTime timestamp
      const startDate = new Date(pass.startTime * 1000);
      const passDay = startDate.toLocaleDateString('en-US', { weekday: 'long' });
      const isValid = passDay === day;
      
      return isValid;
    }
    
    // If pass has week constraints, check if it's valid for the selected day
    if (pass.weekStart) {
      const weekStartDate = new Date(pass.weekStart);
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 7);
      
      // For week-based passes, show them on all days within the week
      const now = new Date();
      const isValid = now >= weekStartDate && now < weekEndDate;
      return isValid;
    }
    
    // If no time constraints, pass is valid for all days
    return true;
  };

  // Determine accessibility for each pass
  const passesWithAccessibility = availablePasses.map(pass => {
    let isAccessible = true;

    // Check if user received this pass as a gift
    const isGiftedPass = userPassClaims.some(claim => 
      claim.passId === pass._id && 
      claim.actionType === "shared" && 
      claim.sharedTo === options.userId
    );


    // Restaurant-specific accessibility (for MapScreen)
    if (restaurants) {
      const eligibleIds =
        pass.eligibleRestaurantIds?.length > 0
          ? pass.eligibleRestaurantIds
          : [pass.restaurant];
      const eligibleRestaurantsInScope = eligibleIds
        .map((id) => restaurants.find((r) => String(r._id) === String(id)))
        .filter(Boolean);

      if (eligibleRestaurantsInScope.length === 0) {
        isAccessible = false;
      } else if (pass.tierRequired && !isGiftedPass) {
        const meetsTierAtRestaurant = (restaurant) => {
          const userMembership = userMemberships?.find(
            (membership) => membership.restaurant === restaurant.name
          );
          const userTierLevel = userMembership
            ? tierHierarchy[userMembership.tier] || 0
            : 0;
          const requiredTierLevel = tierHierarchy[pass.tierRequired] || 0;

          const userPointsForRestaurant =
            userPointsForRestaurants?.find(
              (pointsData) =>
                String(pointsData.restaurantId) === String(restaurant._id)
            )?.points || 0;

          const requiredTier = restaurant.membershipTiers?.find(
            (tier) => tier.tier === pass.tierRequired
          );

          if (!requiredTier) return false;
          const hasEnoughPoints =
            userPointsForRestaurant >= requiredTier.checkinRequirement;
          const hasRequiredTierMembership =
            userMembership && userTierLevel >= requiredTierLevel;
          return hasEnoughPoints || hasRequiredTierMembership;
        };

        isAccessible = eligibleRestaurantsInScope.some(meetsTierAtRestaurant);
      } else if (isGiftedPass) {
        isAccessible = true;
      }
    }

    // Restaurant-specific accessibility (for PassesComponent)
    if (selectedRestaurant && userTier && userPoints !== undefined) {
      if (pass.tierRequired && !isGiftedPass) {
        // Only check tier requirements if the pass wasn't gifted to the user
        const userTierLevel = tierHierarchy[userTier] || 0;
        const requiredTierLevel = tierHierarchy[pass.tierRequired] || 0;
        isAccessible = userTierLevel >= requiredTierLevel;
      } else if (isGiftedPass) {
        isAccessible = true;
      }
    }

    return { ...pass, isAccessible, isGiftedPass };
  });

  // Categorize passes
  const downPasses = [];
  const locationPasses = [];
  const availableNowPasses = [];
  const notAvailablePasses = [];

  passesWithAccessibility.forEach(pass => {
    // Skip passes that have been claimed by the user
    // But allow gifted passes (where user is the recipient) to be shown
    if (claimedPassIds.has(pass._id)) {
      return;
    }
    
    // Skip passes that the user gifted to others (but not passes gifted to them)
    if (giftedPassIds.has(pass._id) && !pass.isGiftedPass) {
      return;
    }

    // Filter by selected day if specified
    if (selectedDay && !isPassValidForDay(pass, selectedDay)) {
      return;
    }

    // Add timeframe information to each pass
    const now = new Date();

    let isCurrentlyValid = true;
    let timeframeReason = null;

    if (pass.startTime && pass.endTime) {
      // The pass's weekday is encoded in the startTime/endTime timestamps
      // (see isPassValidForDay above). "Live now" therefore requires today to
      // match the pass's weekday AND the current time-of-day to fall inside
      // the window. For windows that cross midnight, the pass is also live
      // on the day after its start day, before endMinutes.
      const startDate = new Date(pass.startTime * 1000);
      const endDate = new Date(pass.endTime * 1000);

      const todayDay = now.toLocaleDateString('en-US', { weekday: 'long' });
      const passStartDay = startDate.toLocaleDateString('en-US', { weekday: 'long' });
      const passEndDay = endDate.toLocaleDateString('en-US', { weekday: 'long' });

      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const startMinutes = startDate.getHours() * 60 + startDate.getMinutes();
      const endMinutes = endDate.getHours() * 60 + endDate.getMinutes();

      let isValidTime;
      if (startMinutes <= endMinutes) {
        // Non-wrapping window: both endpoints sit on passStartDay.
        isValidTime =
          todayDay === passStartDay &&
          currentMinutes >= startMinutes &&
          currentMinutes <= endMinutes;
      } else {
        // Window crosses midnight: live late on the start day OR early on the next day.
        const inLateStartWindow =
          todayDay === passStartDay && currentMinutes >= startMinutes;
        const inEarlyEndWindow =
          todayDay === passEndDay && currentMinutes <= endMinutes;
        isValidTime = inLateStartWindow || inEarlyEndWindow;
      }

      if (!isValidTime) {
        isCurrentlyValid = false;
        const startTimeStr = startDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        const endTimeStr = endDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        timeframeReason = `${startTimeStr}-${endTimeStr}`;
      }
    }

    if (pass.weekStart) {
      const weekStartDate = new Date(pass.weekStart);
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 7);

      if (now < weekStartDate || now >= weekEndDate) {
        isCurrentlyValid = false;
        timeframeReason = `Week of ${weekStartDate.toLocaleDateString()}`;
      }
    }

    const passWithTimeframe = { ...pass, isCurrentlyValid, timeframeReason };

    // 1. "Down" passes (user is down for these) - highest priority
    if (downPassIds.has(pass._id)) {
      downPasses.push(passWithTimeframe);
    }
    // 2. Location-based passes (accessible and not down) - medium priority
    else if (pass.isAccessible) {
      locationPasses.push(passWithTimeframe);
    }
    // 3. Available Now vs Not Available - for timeframe filtering
    if (isCurrentlyValid) {
      availableNowPasses.push(passWithTimeframe);
    } else {
      notAvailablePasses.push(passWithTimeframe);
    }
  });

  const result = {
    down: downPasses,
    location: locationPasses,
    availableNow: availableNowPasses,
    notAvailable: notAvailablePasses
  };

  return result;
};

/**
 * React hook for filtering unused passes
 */
export const useUnusedPasses = (availablePasses, userPassClaims, options = {}) => {
  return React.useMemo(() => {
    return filterUnusedPasses(availablePasses, userPassClaims, options);
  }, [availablePasses, userPassClaims, options]);
}; 